import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, Loader2, FolderPlus, FolderOpen } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import './list-selection-modal.css';

interface AccountList {
  list_id: string;
  name: string;
  description: string;
  account_count: number;
  created_at: string;
  updated_at: string;
}

interface AccountListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectList: (listId: string) => void;
  onCreateNew: () => void;
  selectedCount: number;
}

export const AccountListModal: React.FC<AccountListModalProps> = ({
  isOpen,
  onClose,
  onSelectList,
  onCreateNew,
  selectedCount
}) => {
  const { getAccountLists } = useApi();
  const [lists, setLists] = useState<AccountList[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      loadLists();
    }
  }, [isOpen]);

  const loadLists = async () => {
    setLoading(true);
    try {
      const result = await getAccountLists(true);
      if (result && typeof result === 'object' && result !== null && 'lists' in result && Array.isArray(result.lists)) {
        setLists(result.lists);
      }
    } catch (err) {
      console.error('Error loading account lists:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleListSelection = useCallback((listId: string) => {
    setSelectedListIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  }, []);


  const handleConfirm = useCallback(() => {
    if (selectedListIds.size === 0) {
      return;
    }
    // For account lists, we'll just select the first one since the API expects a single list
    const firstListId = Array.from(selectedListIds)[0];
    onSelectList(firstListId);
    onClose();
  }, [selectedListIds, onSelectList, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content list-selection-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add to Account List</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="selection-summary">
            <span>Adding {selectedCount} compan{selectedCount !== 1 ? 'ies' : 'y'} to:</span>
          </div>

          {/* Create New List Section */}
          <div className="create-list-section">
            <button
              className="create-list-toggle"
              onClick={onCreateNew}
            >
              <FolderPlus size={16} />
              <span>Create New List</span>
            </button>
          </div>

          {/* Existing Lists Section */}
          <div className="existing-lists-section">
            <h3>Existing Lists</h3>
            
            {loading ? (
              <div className="loading-state">
                <Loader2 size={20} className="spinning" />
                <span>Loading lists...</span>
              </div>
            ) : lists.length === 0 ? (
              <div className="empty-state">
                <FolderOpen size={48} />
                <p>No lists found</p>
                <p>Create your first list to get started</p>
              </div>
            ) : (
              <div className="lists-grid">
                {lists.map(list => (
                    <div
                      key={list.list_id}
                      className={`list-item ${selectedListIds.has(list.list_id) ? 'selected' : ''}`}
                      onClick={() => toggleListSelection(list.list_id)}
                    >
                      <div className="list-item-header">
                        <div className="list-item-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedListIds.has(list.list_id)}
                            onChange={() => toggleListSelection(list.list_id)}
                          />
                          <div className="checkbox-visual">
                            {selectedListIds.has(list.list_id) && <Check size={10} />}
                          </div>
                        </div>
                        <div className="list-item-info">
                          <h4 className="list-name">
                            {list.name}
                          </h4>
                          {list.description && (
                            <div className="list-description">
                              {list.description}
                            </div>
                          )}
                          <div className="list-meta">
                            <span className="contact-count">
                              {list.account_count} account{list.account_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="selection-info">
            {selectedListIds.size > 0 && (
              <span>{selectedListIds.size} list{selectedListIds.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="modal-actions">
            <button className="button-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button-primary"
              onClick={handleConfirm}
              disabled={selectedListIds.size === 0}
            >
              Add to List{selectedListIds.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountListModal;
